import { useAuth } from "@clerk/react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  listAdminUsers,
  type AdminUserListResponse,
  type AdminUserSummary,
} from "@/features/admin/adminApi";

const PAGE_SIZE = 20;

function formatDate(value: string | null): string {
  if (!value) {
    return "Not set";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function formatTier(user: AdminUserSummary): string {
  if (user.tier === "pro") {
    return "Pro";
  }

  if (user.is_lifetime) {
    return "Lifetime Standard";
  }

  return user.tier.charAt(0).toUpperCase() + user.tier.slice(1);
}

function AccessDenied() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        This page is not available
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        User management is only available to authorised operators.
      </p>
    </section>
  );
}

export function AdminUsers() {
  const { getToken, isLoaded } = useAuth();
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [tier, setTier] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminUserListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDenied, setIsDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      q: submittedQuery,
      tier,
      page,
      pageSize: PAGE_SIZE,
    }),
    [page, submittedQuery, tier],
  );

  useEffect(() => {
    async function loadUsers() {
      if (!isLoaded) {
        return;
      }

      setIsLoading(true);
      setIsDenied(false);
      setError(null);

      try {
        const token = await getToken();
        setData(await listAdminUsers(token, filters));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          setIsDenied(true);
          return;
        }

        setError(
          err instanceof Error ? err.message : "Unable to load users right now.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadUsers();
  }, [filters, getToken, isLoaded]);

  if (isDenied) {
    return <AccessDenied />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Admin
      </Link>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Admin</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
          Users
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          Read-only view of accounts, access state and product activity.
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <form
          className="grid gap-3 lg:grid-cols-[1fr_180px_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSubmittedQuery(query.trim());
          }}
        >
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name or email"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
          <select
            value={tier}
            onChange={(event) => {
              setPage(1);
              setTier(event.target.value);
            }}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            <option value="">All tiers</option>
            <option value="free">Free</option>
            <option value="standard">Standard</option>
            <option value="pro">Pro</option>
          </select>
          <button
            type="submit"
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Search
          </button>
        </form>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="h-72 animate-pulse rounded-lg bg-white" />
      ) : (
        <section className="grid gap-3">
          {(data?.users ?? []).map((user) => (
            <article
              key={user.id}
              className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="break-words text-base font-semibold text-slate-950">
                      {user.name}
                    </h3>
                    {user.is_admin ? (
                      <span className="rounded-full bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700">
                        Admin
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 break-words text-sm text-slate-600">
                    {user.email}
                  </p>
                  <p className="mt-2 text-sm font-semibold text-slate-800">
                    {formatTier(user)}
                  </p>
                </div>
                <Link
                  to={`/admin/users/${encodeURIComponent(user.id)}`}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 sm:w-fit"
                >
                  View
                </Link>
              </div>
              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3 lg:grid-cols-5">
                <div>
                  <p className="text-slate-500">Projects</p>
                  <p className="font-semibold text-slate-950">
                    {user.projectCount}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Blueprints</p>
                  <p className="font-semibold text-slate-950">
                    {user.blueprintCount}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Tool results</p>
                  <p className="font-semibold text-slate-950">
                    {user.toolResultCount}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Joined</p>
                  <p className="font-semibold text-slate-950">
                    {formatDate(user.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Updated</p>
                  <p className="font-semibold text-slate-950">
                    {formatDate(user.updated_at)}
                  </p>
                </div>
              </div>
            </article>
          ))}

          {data?.users.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="font-semibold text-slate-950">No users found</p>
              <p className="mt-2 text-sm text-slate-600">
                Try a different search or tier filter.
              </p>
            </div>
          ) : null}
        </section>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={page <= 1 || isLoading}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <p className="text-sm text-slate-600">Page {page}</p>
        <button
          type="button"
          disabled={!data?.pagination.hasNextPage || isLoading}
          onClick={() => setPage((current) => current + 1)}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
